/**
 * PR-7b — Armado de hojas/workbook del Excel de agencia, extraído de
 * `LiquidacionesAgenciaComponent` (`agregarHojasLiquidacionAlWorkbook` y
 * `sanitizeSheetName`).
 *
 * Puro — sin Angular ni HTTP — para poder probarse sin TestBed. El componente
 * conserva la parte de I/O real (`crearYDescargarExcel`: logo por `fetch`,
 * blob, descarga por `<a>.click()`), que no es separable sin cambiar
 * comportamiento. Ver
 * `liquidaciones-agencia.component.excel-characterization.spec.ts` (PR-7a)
 * para la red de seguridad de comportamiento end-to-end.
 */
import * as ExcelJS from 'exceljs';
import {
    conTotalesAlFinal,
    debeFiltrarColumnasDeServicios,
    unionKeysPreservingOrder,
} from './liquidaciones-agencia-excel-columnas.util';
import {
    aplicarEstilosAhojaExcel,
    capitalizeHeader,
    filtrarColumnasConServiciosPresentes,
    getColumnWidth,
} from './liquidaciones-agencia-excel-estilos.util';

/** Sanitiza nombre de hoja Excel (máx 31 caracteres, sin \ / ? * [ ]) */
export function sanitizeSheetName(name: string): string {
    const sanitized = name.replace(/[/:*?\\[\]]/g, '-').trim();
    return sanitized.length > 31 ? sanitized.substring(0, 31) : sanitized;
}

/**
 * Agrega las hojas de una liquidación al workbook (Totales, Agenciamiento, Pallets, etc.).
 * Si prefijo es no nulo, los nombres de hoja serán "prefijo - Totales", etc. (para Excel fusionado).
 *
 * `onLogoError` se reenvía tal cual a `aplicarEstilosAhojaExcel` — ver ese
 * archivo para el motivo (este módulo no depende de `LoggerService`).
 */
export async function agregarHojasLiquidacionAlWorkbook(
    workbook: ExcelJS.Workbook,
    data: any,
    prefijo: string | null,
    colors: Record<string, string>,
    logoArrayBuffer: ArrayBuffer | null,
    onLogoError?: (error: unknown) => void
): Promise<void> {
    const sheetName = (label: string): string =>
        prefijo ? sanitizeSheetName(`${prefijo} - ${label}`) : label;

    if (
        data.consolidado &&
        data.consolidado.rows &&
        data.consolidado.rows.length > 0
    ) {
        const wsConsolidado = workbook.addWorksheet(sheetName('Consolidado'));
        // Punto C (OPB, ago-2026): los identificadores de documento abren la hoja
        // — N° Factura, Nota de Cobro, Nro. Liquidación — y con los MISMOS rótulos
        // en las tres hojas. Acá los rótulos ya eran los correctos; sólo cambia el
        // orden. Los que se alinean a éstos son Agenciamiento y Transportes, vía
        // `headerMap`.
        const headers = [
            {
                header: 'N° Factura',
                key: 'nroFacturaVenta',
                width: 18,
            },
            {
                header: 'Nota de Cobro',
                key: 'nroNotaCobro',
                width: 18,
            },
            {
                header: 'Nro. Liquidación',
                key: 'nroLiquidacion',
                width: 18,
            },
            { header: 'Contenedores', key: 'contenedor', width: 22 },
            { header: 'Cliente', key: 'cliente', width: 30 },
            { header: 'Nave', key: 'nave', width: 25 },
            { header: 'POL', key: 'pol', width: 20 },
            { header: 'POD', key: 'pod', width: 20 },
            { header: 'ETD', key: 'etd', width: 14 },
            { header: 'Reserva', key: 'reserva', width: 18 },
            { header: 'Ref. Cliente', key: 'refCliente', width: 18 },
            { header: 'Naviera', key: 'naviera', width: 22 },
            { header: 'Tipo Embarque', key: 'tipoEmbarque', width: 18 },
            { header: 'PLTS', key: 'plts', width: 10 },
            { header: 'Agenciamiento', key: 'agenciamiento', width: 18 },
            // ERR-Nuevo-1 / MEJ-02 (OPB jul-2026): el consolidado va SIMPLE, sin desglose de
            // Gate Out/In ni DUS no legalizado. Esos montos ruedan a "OS Venta" (Gate vía el
            // total de transporte; DUS ya incluido en osVenta vía totalVentaAg del agenciamiento).
            // El desglose Gate Out/In vive en la hoja Transporte; el "Dus no embarcado" en Agenciamiento.
            { header: 'Transporte', key: 'costoTransporte', width: 18 },
            { header: 'OS Venta', key: 'osVenta', width: 18 },
            { header: 'Gestión OPB', key: 'gestionOpb', width: 16 },
            { header: 'Total Neto', key: 'totalVenta', width: 18 },
            { header: 'Total Traspaso', key: 'totalTraspaso', width: 18 },
            { header: 'Total General', key: 'totalGeneral', width: 18 },
        ];
        await aplicarEstilosAhojaExcel(
            workbook,
            wsConsolidado,
            'Consolidado',
            data.consolidado.rows,
            headers,
            colors,
            logoArrayBuffer,
            onLogoError
        );
    }
    if (data.totales && data.totales.rows && data.totales.rows.length > 0) {
        const wsTotales = workbook.addWorksheet(sheetName('Totales'));
        // ERR-04: unión de claves de TODAS las filas (no solo la primera) —
        // ver comentario extenso en la hoja Transportes más abajo.
        // OPB email 2026-10-09 19:36 (Punto C): Gestión OPB/Total Venta/Total
        // Traspaso siempre al final.
        const headers = conTotalesAlFinal(
            unionKeysPreservingOrder(data.totales.rows)
        ).map((key) => ({
            header: capitalizeHeader(key),
            key: key,
            width: getColumnWidth(key),
        }));
        await aplicarEstilosAhojaExcel(
            workbook,
            wsTotales,
            'Totales',
            data.totales.rows,
            headers,
            colors,
            logoArrayBuffer,
            onLogoError
        );
    }
    if (
        data.contenedores &&
        data.contenedores.rows &&
        data.contenedores.rows.length > 0
    ) {
        const wsAgenciamiento = workbook.addWorksheet(
            sheetName('Agenciamiento')
        );
        // ERR-04: unión de claves de TODAS las filas (no solo la primera) —
        // ver comentario extenso en la hoja Transportes más abajo.
        let allKeys = unionKeysPreservingOrder(data.contenedores.rows);

        // Filtrar columnas dinámicas de servicios si está disponible serviciosPresentes.
        // Ver comentario extenso en la hoja Transportes: un array VACÍO significa
        // "ningún servicio tiene monto" y SÍ debe filtrar (regla de negocio 11).
        const serviciosPresentes = data.contenedores.serviciosPresentes;
        if (debeFiltrarColumnasDeServicios(serviciosPresentes)) {
            allKeys = filtrarColumnasConServiciosPresentes(
                allKeys,
                serviciosPresentes
            );
        }

        // OPB email 2026-10-09 19:36 (Punto C): Gestión OPB/Total Venta/Total
        // Traspaso siempre al final, incluso si una columna dinámica de
        // servicio quedó antes por el orden de aparición en el consolidado.
        allKeys = conTotalesAlFinal(allKeys);

        const headers = allKeys.map((key) => ({
            header: capitalizeHeader(key),
            key: key,
            width: getColumnWidth(key),
        }));
        await aplicarEstilosAhojaExcel(
            workbook,
            wsAgenciamiento,
            'Agenciamiento',
            data.contenedores.rows,
            headers,
            colors,
            logoArrayBuffer,
            onLogoError
        );
    }
    if (data.pallets && data.pallets.rows && data.pallets.rows.length > 0) {
        const wsPallets = workbook.addWorksheet(sheetName('Pallets'));
        // ERR-04: unión de claves de TODAS las filas (no solo la primera) —
        // ver comentario extenso en la hoja Transportes más abajo.
        // OPB email 2026-10-09 19:36 (Punto C): Gestión OPB/Total Venta/Total
        // Traspaso siempre al final.
        const headers = conTotalesAlFinal(
            unionKeysPreservingOrder(data.pallets.rows)
        ).map((key) => ({
            header: capitalizeHeader(key),
            key: key,
            width: getColumnWidth(key),
        }));
        await aplicarEstilosAhojaExcel(
            workbook,
            wsPallets,
            'Pallets',
            data.pallets.rows,
            headers,
            colors,
            logoArrayBuffer,
            onLogoError
        );
    }
    if (
        data.serviciosExtra &&
        data.serviciosExtra.rows &&
        data.serviciosExtra.rows.length > 0
    ) {
        const wsServicios = workbook.addWorksheet(sheetName('Servicios Extra'));
        // ERR-04: unión de claves de TODAS las filas (no solo la primera) —
        // ver comentario extenso en la hoja Transportes más abajo.
        // OPB email 2026-10-09 19:36 (Punto C): Gestión OPB/Total Venta/Total
        // Traspaso siempre al final.
        const headers = conTotalesAlFinal(
            unionKeysPreservingOrder(data.serviciosExtra.rows)
        ).map((key) => ({
            header: capitalizeHeader(key),
            key: key,
            width: getColumnWidth(key),
        }));
        await aplicarEstilosAhojaExcel(
            workbook,
            wsServicios,
            'Servicios Extra',
            data.serviciosExtra.rows,
            headers,
            colors,
            logoArrayBuffer,
            onLogoError
        );
    }
    if (
        data.transportes &&
        data.transportes.rows &&
        data.transportes.rows.length > 0
    ) {
        const wsTransportes = workbook.addWorksheet(sheetName('Transportes'));
        // ERR-04 (OPB 2026-09-30 11:34): unión de claves de TODAS las
        // filas, no solo `Object.keys(firstRow)`. La exportación
        // combinada de varias liquidaciones (`combinedData`, más arriba)
        // aplana las filas de todas las liquidaciones seleccionadas con
        // `flatMap`: si la primera fila del arreglo combinado pertenece a
        // una liquidación que no generó una columna de servicio dinámico
        // (p.ej. "Porteos"), esa columna desaparecía del Excel aunque su
        // monto sí sumara al Total Venta y otra liquidación del mismo
        // consolidado sí la tuviera. Caso real: cliente EUROFISH, liqs
        // 1769/1775/1780 (solo transporte, sin packing lists) —
        // "Porteos" y "Falso Flete Depósito" no aparecían en el Excel
        // combinado. Ver `unionKeysPreservingOrder` para el detalle del
        // orden de columnas resultante.
        let allKeys = unionKeysPreservingOrder(data.transportes.rows);

        // Filtrar columnas dinámicas de servicios si está disponible serviciosPresentes.
        // Regla de negocio 11 (MEJ-08): las columnas con todo en 0 NO se renderizan.
        // OJO: un array VACÍO es información válida ("ningún servicio tiene monto"),
        // NO ausencia de información. Guardar con `.length > 0` hacía que una
        // liquidación sin servicios adicionales mostrara la sábana completa de
        // columnas en 0 (reporte cliente 2026-07-27, liq 1612). Solo se omite el
        // filtro si el backend no envió el campo (compatibilidad con respuestas viejas).
        const serviciosPresentes = data.transportes.serviciosPresentes;
        if (debeFiltrarColumnasDeServicios(serviciosPresentes)) {
            allKeys = filtrarColumnasConServiciosPresentes(
                allKeys,
                serviciosPresentes
            );
        }

        // OPB email 2026-10-09 19:36 (Punto C): Gestión OPB/Total Venta/Total
        // Traspaso siempre al final, incluso si una columna dinámica de
        // servicio quedó antes por el orden de aparición en el consolidado.
        allKeys = conTotalesAlFinal(allKeys);

        const headers = allKeys.map((key) => ({
            header: capitalizeHeader(key),
            key: key,
            width: getColumnWidth(key),
        }));
        await aplicarEstilosAhojaExcel(
            workbook,
            wsTransportes,
            'Transportes',
            data.transportes.rows,
            headers,
            colors,
            logoArrayBuffer,
            onLogoError
        );
    }
}
